
import { defineComponent, reactive, onBeforeMount } from 'vue'
import { useRouter, useRoute } from 'vue-router'

// components
import PageTitle from '@/common/components/UI/PageTitle/index.vue'
import TheButton from '@/common/components/UI/TheButton/index.vue'
import TheInput from '@/common/components/UI/TheInput/index.vue'
import TheSelect from '@/common/components/UI/TheSelect/index.vue'
import FileReader from '@/common/components/UI/FileReader/index.vue'
import Alert from '@/common/components/UI/Alert/index.vue'
import Loader from '@/common/components/UI/Loader/index.vue'

// quill
import { QuillEditor } from '@vueup/vue-quill'
import ImageUploader from 'quill-image-uploader'
import '@vueup/vue-quill/dist/vue-quill.snow.css'

// validation
import useVuelidate from '@vuelidate/core'
import { required, maxLength, integer } from '@vuelidate/validators'

// realisations
import { cases } from '@/core/realisations/cases.realisation'
import { vacancyUploadFile } from '@/new-services/files.service'

// types
import { CaseDetailsT } from '@/core/types/cases.types'

// composable
import useNotifications from '@/common/composable/useNotifications'

const TITLE_MAX_LENGTH = 110

const STATUSES = [
    { id: 1, name: 'Активный' },
    { id: 2, name: 'Не активный' }
]

export default defineComponent({
    components: {
        PageTitle,
        TheButton,
        TheInput,
        TheSelect,
        FileReader,
        'quill-editor': QuillEditor,
        Alert,
        Loader
    },
    setup() {
        const notifications = useNotifications()
        const router = useRouter()
        const route = useRoute()

        const caseId = Number(route.params.case_id as string)

        const case_state = reactive({
            is_loading: false,
            is_failed: false,
            case: null as CaseDetailsT | null
        })

        const state = reactive({
            is_loading: false,
            statusId: null as number | null,
            shortTitle: null as string | null,
            title: null as string | null,
            feedbackHeader: null as string | null,
            shortDescription: null as string | null,
            description: null as string | null,
            previewImage: [] as File[],
            mainImage: [] as File[],
            order: null as string | null,
            titleForDisplay: null as string | null,
            descriptionForDisplay: null as string | null,
        })

        const state_form$ = useVuelidate({
            statusId: { required },
            shortTitle: { required, maxLength: maxLength(TITLE_MAX_LENGTH) },
            title: { required },
            feedbackHeader: { required },
            description: { required },
            shortDescription: { required },
            order: { required, integer },
        }, state)

        const loadCase = async () => {
            try {
                case_state.is_loading = true

                const response = await cases.getCase(caseId)
                case_state.case = response.data

                state.statusId = response.data.statusId
                state.shortTitle = response.data.shortTitle
                state.title = response.data.title
                state.feedbackHeader = response.data.feedbackHeader
                state.shortDescription = response.data.shortDescription
                state.description = response.data.description
                state.order = response.data.order
                state.titleForDisplay = response.data.titleForDisplay
                state.descriptionForDisplay = response.data.descriptionForDisplay

                case_state.is_failed = false
            } catch (error) {
                console.error(error)
                case_state.is_failed = true
                notifications.notifyError({ error, title: 'При получении данных о кейсе произошла ошибка' })
            } finally {
                case_state.is_loading = false
            }
        }

        onBeforeMount(loadCase)

        const submit = async () => {
            if (state.is_loading) return

            state_form$.value.$touch()
            if (state_form$.value.$invalid) return

            try {
                state.is_loading = true

                const formData = new FormData()
                formData.append('statusId', (state.statusId as number).toString())
                formData.append('shortTitle', state.shortTitle as string)
                formData.append('title', state.title as string)
                formData.append('feedbackHeader', state.feedbackHeader as string)
                formData.append('shortDescription', state.shortDescription as string)
                formData.append('description', state.description as string)
                formData.append('order', state.order as string)

                if (state.titleForDisplay) {
                    formData.append('titleForDisplay', state.titleForDisplay as string)
                }

                if (state.descriptionForDisplay) {
                    formData.append('descriptionForDisplay', state.descriptionForDisplay as string)
                }

                if (state.previewImage && state.previewImage.length) {
                    formData.append('previewImage', state.previewImage[0])
                }
                
                if (state.mainImage && state.mainImage.length) {
                    formData.append('mainImage', state.mainImage[0])
                }

                await cases.updateCase(caseId, formData)

                router.push('/cases')
            } catch (error) {
                console.error(error)
                notifications.notifyError({ error, title: 'При редактировании кейса произошла ошибка' })
            } finally {
                state.is_loading = false
            }
        }

        const quillModules = {
            name: 'imageUploader',
            module: ImageUploader,
            options: {
                upload: (file: File) => {
                    return new Promise(async (resolve, reject) => {
                        try {
                            const form_data = new FormData()
                            form_data.append('file', file)
                            const { data } = await vacancyUploadFile(form_data)

                            resolve(data.data.url)
                        } catch (error) {
                            notifications.notifyError({ error, title: 'При загрузке изображения произошла ошибка' })
                            reject(error)
                        }
                    })
                }
            }
        }

        return { caseId, state, case_state, state_form$, submit, loadCase, STATUSES, quillModules }
    }
})
